import * as Square from "@square/web-sdk";

// Fields whose validity must be confirmed `true` before we treat the
// Square card form as complete enough to submit.
//
// `postalCode` is deliberately excluded from this list because it is
// country-adaptive: Square hides the field entirely for JP/CN-issued cards
// and reformats it (e.g. 5-digit ZIP to 4-digit postcode) when an AU/CA
// BIN is detected. We cannot reliably observe every one of those
// transitions via events, and gating on `postalCode` has previously left
// users stuck with a permanently disabled submit button. The authoritative
// validator at submit time is `card.tokenize()` (see useTokenizeCard), so
// treating `postalCode` as "disable only when Square explicitly says so"
// is safe here.
export const REQUIRED_SQUARE_FORM_FIELDS: Square.CardFieldNamesValues[] = [
  "cardNumber",
  "cvv",
  "expirationDate",
];

// A field in `SquareFormFieldValidity` can be in one of three states:
//
// - `true`  : Square has explicitly reported the field as completely valid.
// - `false` : Square has explicitly reported the field as invalid.
// - absent  : Square has never emitted a validity event for the field. We
//             treat this as "unknown", not as "invalid" — see
//             `computeSquareFormValidity` for why.
export type SquareFormFieldValidity = Partial<
  Record<Square.CardFieldNamesValues, boolean>
>;

/**
 * Decides whether the Square card form is in a valid-enough state to enable
 * the submit button.
 *
 * Rules (both must hold):
 *  1. No field is explicitly `false`.
 *  2. Every field in `REQUIRED_SQUARE_FORM_FIELDS` is explicitly `true`.
 *
 * A field that is `undefined` is treated as "unknown" rather than "invalid",
 * which deliberately avoids deadlocking the submit button when a field
 * never emits an observable event (most commonly the country-adaptive
 * `postalCode`).
 */
export const computeSquareFormValidity = (
  fieldValidity: SquareFormFieldValidity
): boolean => {
  const anyExplicitlyInvalid = (
    Object.values(fieldValidity) as (boolean | undefined)[]
  ).some((value) => value === false);
  if (anyExplicitlyInvalid) return false;

  return REQUIRED_SQUARE_FORM_FIELDS.every(
    (field) => fieldValidity[field] === true
  );
};
